import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Offcanvas,
  Table,
  Spinner,
  CardHeader,
} from 'reactstrap';
import Icon from '@mdi/react';
import {
  mdiClockPlusOutline,
  mdiNoteEditOutline,
  mdiFileDownloadOutline,
} from '@mdi/js';
import { toast } from 'react-toastify';
import {
  FilterDropdown,
  AsyncAutocompleteSelect,
  ErrorModalWrapper,
  ErrorTable,
  TableFilterBubbles,
  TableSortDisplayArrow,
} from '../../components/Common';
import { useTableHook } from '../../components/hooks';
import { fetchAttendanceDetails } from '../../store/apis/attendanceService';
import { fetchTableColumnValues } from '../../store/apis/commonService';
import { fetchManualEventList } from '../../store/apis/eventService';
import { AuthUtil, DateUtil, ExportUtil } from '../../utils';
import {
  tableColumns,
  dataConfig,
  renderTimesheetStatusLegend,
  MONTHS,
} from './constants';
import ManualPunchIn from '../../components/Widgets/PunchInWidget/ManualPunchIn';
import { Messages } from '../../common/messages';
import './TimesheetStatistic.scss';

export default function TimesheetStatistics() {
  document.title = 'Time Sheet Summary | Envision';
  const location = useLocation();
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth();
  const [punchInOffcanvas, setPunchInOffcanvas] = useState(false);
  const [selectRowData, setSelectRowData] = useState(null);
  const [setCanvasType, setSetCanvasType] = useState('create');
  const [manualEventData, setManualEventData] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorData, setErrorData] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const year = Number(searchParams.get('Year')) || Number(thisYear);
  const month = Number(searchParams.get('Month')) - 1 ?? thisMonth;
  const { startDate, endDate } = DateUtil.getMonthRange(month, year);

  const initalData = {
    employeeId: AuthUtil.getAuthUser()?.employeeId,
    fromDate: startDate,
    toDate: endDate,
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleSetExternalFilters,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    isLoadingColumn,
    isLoadingData,
    handleAutoSort,
    selectAllRows,
    handleRowCheck,
    activeRows,
    tableDataMutation,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 0,
      sortColumn: 'Dt',
      sortDirection: 'ASC',
      filters: [],
      type: 'DETAIL_MISSING',
      ...initalData,
    },
    fetchFn: fetchAttendanceDetails,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'AttendanceEnquiry',
    columnKeyPrefix: 'AttendanceEnquiryColumn',
    tableName: 'AttendanceEnquiry',
    defaultExternalFilters: {
      month,
      employeeData: {
        label: AuthUtil.getAuthUser()?.userFullName,
        value: AuthUtil.getAuthUser()?.employeeId,
      },
    },
  });

  // Fetch manual event list
  const fetchManualEventMutation = useMutation({
    mutationFn: fetchManualEventList,
  });

  const togglePunchIn = () => {
    setPunchInOffcanvas(!punchInOffcanvas);
  };

  const handleFetchEvents = async entry => {
    const eventParams = {
      startDate: entry.Dt,
    };

    try {
      const eventData = await fetchManualEventMutation.mutateAsync(eventParams);
      if (eventData && eventData.length === 1) {
        setManualEventData(eventData[0]);
        setSelectRowData(entry);
        setSetCanvasType('update');
        togglePunchIn();
      } else if (eventData && eventData.length === 0) {
        toast.error('No event data found for the selected date.', {
          className: 'toast-error',
        });
      }
    } catch (error) {
      toast.error('Error fetching event data.', {
        className: 'toast-error',
      });
    }
  };

  const externalFiltersValidation = () => {
    if (!externalFilters.month) {
      toast.error('Please select a Month', {
        className: 'toast-error',
      });
      return false;
    }
    if (!externalFilters.employeeData) {
      toast.error('Please select an Employee', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  // Handle the error modal
  const handleErrorModal = errorResponseData => {
    setIsErrorModalOpen(true);
    setErrorData(errorResponseData);
  };

  const renderTableBody = () => {
    const tableData = data?.data;
    // Ensure tableData is not undefined or null
    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={10}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_TIMESHEET
            )}
          </td>
        </tr>
      );
    }

    const rowBackgroundColor = {
      Holiday: '#EEEEEE',
      Today: '#C6E4CB',
    };

    return tableData?.map((entry, index) => (
      <tr
        key={entry.id}
        style={{
          backgroundColor: rowBackgroundColor[entry.Status] || '',
        }}
      >
        <td className="py-2">
          <div className="form-check">
            <Input
              className="form-check-input blue-checkbox"
              type="checkbox"
              name="chk_child"
              checked={activeRows?.includes(index)}
              onChange={e => handleRowCheck(index, e.target.checked)}
            />
          </div>
        </td>
        <td className="px-2 py-2">{entry.Dt}</td>
        <td className="px-2 py-2">{entry.DayName || '-'}</td>
        <td className="px-2 py-2">{entry.TimeIn || '-'}</td>
        <td className="px-2 py-2">{entry.TimeOut || '-'}</td>
        <td className="px-2 py-2">{entry.Hrs || '-'}</td>
        <td className="px-2 py-2">{entry.M_TimeIn || '-'}</td>
        <td className="px-2 py-2">{entry.M_TimeOut || '-'}</td>
        <td className="px-2 py-2">{entry.M_Hrs}</td>
        <td className="px-2 py-2 text-center">
          {renderTimesheetStatusLegend(entry.Status)}
        </td>
        <td className="px-2 py-2 text-center">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Button
              size="sm"
              className="non-btn"
              color="link"
              onClick={() => {
                handleFetchEvents(entry);
              }}
            >
              <Icon
                path={mdiNoteEditOutline}
                size={0.8}
                color="#017EBD"
                title="Please click to edit the manual timesheet entry"
              />
            </Button>
            <Button
              size="sm"
              className="non-btn"
              color="link"
              onClick={() => {
                setSelectRowData(entry);
                setSetCanvasType('create');
                togglePunchIn();
              }}
            >
              <Icon
                path={mdiClockPlusOutline}
                size={0.8}
                color="#079455"
                title="Please click to add a manual timesheet entry"
              />
            </Button>
          </div>
        </td>
      </tr>
    ));
  };

  const handleTimesheetStatisticsSearch = async () => {
    const rangeDate = DateUtil.getMonthRange(
      Number(externalFilters?.month),
      Number(year),
    );
    setPageRequest(prev => ({
      ...prev,
      employeeId: externalFilters?.employeeData.value,
      fromDate: rangeDate?.startDate,
      toDate: rangeDate?.endDate,
    }));
  };

  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    let selectedData = exportResponseData?.data;

    if (activeRows?.length > 0) {
      selectedData = activeRows.map(index => exportResponseData?.data[index]);
      if (selectedData.length === 0) {
        toast.error('No selected data found to export', {
          className: 'toast-error',
        });
        return;
      }
    }

    ExportUtil.downloadExcel(selectedData, {
      fileName: `TimeSheet_Enquiry_${timesTamp}.xlsx`,
      columns: {
        Dt: 'Date',
        DayName: 'Day',
        TimeIn: 'Time In',
        TimeOut: 'Time Out',
        Hrs: 'Hours',
        M_TimeIn: 'Manual Time In',
        M_TimeOut: 'Manual Time Out',
        M_Hrs: 'Manual Hours',
        Status: 'Status',
      },
    });
  };

  return (
    <div className="page-content timesheet-statistics-page">
      <Card className="timesheet-statistics-card">
        <CardHeader
          className="d-flex justify-content-between align-items-center pt-2 pb-2"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <div>
            <span className="fs-5" style={{ color: '#000', fontWeight: '600' }}>
              Time Sheet Statistics
            </span>
          </div>
          <div className="d-flex gap-2">
            <Button className="custom-light-btn" onClick={handleExportExcel}>
              <span>Download as Excel</span>
              <Icon path={mdiFileDownloadOutline} size={0.8} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="external-filter gap-2 mx-2 mt-2 p-3 d-flex flex-row">
            <div className="d-flex flex-row justify-content-between align-items-center gap-2">
              <Col className="external-filter-col">
                <b>Employee ID</b>
                <AsyncAutocompleteSelect
                  autoCompleteFn={null}
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Name,
                    }))
                  }
                  onChange={selectedOption => {
                    handleSetExternalFilters('employeeData', selectedOption);
                  }}
                  value={externalFilters?.employeeData}
                  isClearable={false}
                />
              </Col>
              <Col className="external-filter-col">
                <b>Month</b>
                <Input
                  className="external-filter-input"
                  type="select"
                  id="month"
                  value={externalFilters?.month}
                  onChange={e =>
                    handleSetExternalFilters('month', e.target.value)
                  }
                >
                  <option value="">Select Month</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>
              </Col>
            </div>
            <Col className="d-flex align-items-center justify-content-end">
              <div className="external-filter-btns gap-2">
                <button
                  onClick={() => {
                    if (!externalFiltersValidation()) {
                      return;
                    }
                    handleTimesheetStatisticsSearch();
                  }}
                  className="custom-btn"
                  type="button"
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive timesheet-statistics-table-container">
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  <th>
                    <div className="form-check px-0">
                      <Input
                        className="form-check-input blue-checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={
                          activeRows?.length > 0 &&
                          activeRows?.length === data?.data?.length
                        }
                        onChange={e => selectAllRows(e?.target?.checked)}
                      />
                    </div>
                  </th>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th
                      key={key}
                      className={`px-2 ${key === 'Status' ? 'text-center' : ''}`}
                    >
                      <div
                        className={`d-flex flex-row align-items-center gap-2 ${key === 'Status' ? 'justify-content-center' : 'justify-content-start'}`}
                      >
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      <Offcanvas
        isOpen={punchInOffcanvas}
        toggle={() => setPunchInOffcanvas(!punchInOffcanvas)}
        direction="end"
        style={{ width: '40vw' }}
      >
        <ManualPunchIn
          toggle={togglePunchIn}
          setPunchInOffcanvas={setPunchInOffcanvas}
          data={selectRowData}
          type={setCanvasType}
          eventData={manualEventData}
          handleErrorModal={handleErrorModal}
        />
      </Offcanvas>
      <ErrorModalWrapper
        isModalOpen={isErrorModalOpen}
        toggle={() => setIsErrorModalOpen(!isErrorModalOpen)}
        errorMessage={errorData?.message}
        onButtonClick={() => setIsErrorModalOpen(!isErrorModalOpen)}
      >
        <ErrorTable errorData={errorData?.errorData} dataConfig={dataConfig} />
      </ErrorModalWrapper>
    </div>
  );
}
